import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { profileSelectors } from 'app-components/flux/profile';
import { userInfoActions } from 'app-components/flux/user-info';
import { getEnvironmentConfig, platform, tracker } from 'app-components/utils/core';
import { localPreferences } from 'app-components/utils/core';

import DarkMode from '@mui/icons-material/DarkMode';
import LightMode from '@mui/icons-material/LightMode';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import Dump from 'components/Dump';
import useQPreferences from 'components/QPreferences/useQPreferences';
import QSwitch from 'components/QSwitch';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';

import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';

const useStyles = makeStyles()((theme) => {
  const { spacing } = theme;
  return {
    themeContainer: {
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `${spacing(2)} ${spacing(4)} ${spacing(4)}`,
      borderRadius: '8px',
    },
  };
});

function Profile() {
  const navigate = useNavigate();
  const [isChromebook, setIsChromebook] = useState(false);

  const [AIEnabled, setAIEnabled] = usePreferenceV2('aiInsightsOptIn');

  const authSession = useSelector(authSelectors.getAuthSession);
  const darkMode = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'darkMode'));
  const profile = useSelector(profileSelectors.getProfile);
  const subscription = useSelector(subscriptionsSelectors.getActiveSubscription);

  const playBillingFeatureFlag = useSelector(featureFlagsSelectors.getFeatureFlags)?.get('playBilling');
  const aiEnabled = useSelector(featureFlagsSelectors.isAiEnabled);
  const isPlayBillingEnabled = playBillingFeatureFlag && isChromebook;

  const { setDatasetPreference } = useQPreferences();

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  useMount(async () => {
    const featureFlagsOverrides = localPreferences.getItem('featureFlagsOverrides') || null;
    if (!featureFlagsOverrides?.['business-tier']) {
      dispatch(subscriptionsActions.getSubscriptions());
    }
    dispatch(userInfoActions.getUserInfo());

    const chromebook = await platform.isChromebook();
    setIsChromebook(chromebook);
  });

  const handleAddSubscription = () => {
    if (isPlayBillingEnabled) {
      navigate('/pick-your-plan');
    } else {
      window.location.href = `${getEnvironmentConfig().pick_your_plan_url}?bearer=${authSession.accessToken}`;
    }
  };

  const handleGoToAccount = () => {
    tracker.track(tracker.events.myAccount, { location: 'profile' });
    window.open(`${getEnvironmentConfig().myaccount_url}?hide-nav&bearer=${authSession.accessToken}`);
  };

  const privacyMode = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'privacyMode'));
  const [sPrivacyMode, setSPrivacyMode] = usePreferenceV2('sPrivacyMode');

  return (
    <>
      <SettingsHeader
        title='General'
        description='Manage your basic profile preferences and app settings.'
        primaryItem={
          <Button id='edit-details' onClick={handleGoToAccount} endIcon={<OpenInNewRoundedIcon />} variant='outlined'>
            Manage Quicken ID
          </Button>
        }
      />
      <Stack gap={2}>
        <Card>
          <Dump obj={[profile, subscription]} />
          <CardHeader
            title='Account details'
            titleTypographyProps={{ variant: 'titleMedium' }}
            subheader='Shared across all Quicken products'
            subheaderTypographyProps={{ variant: 'paragraphRegular', color: 'textSecondary' }}
            sx={{ paddingBottom: 1 }}
          />
          <CardContent sx={{ paddingTop: 1 }}>
            <Stack gap={2} justifyContent={'flex-start'} alignItems='flex-start'>
              <Stack>
                <Typography variant='captionRegular' color='text.secondary'>
                  Quicken ID
                </Typography>
                <Typography id='user-details-id' variant='bodyRegular'>
                  {profile && profile.username}
                </Typography>
              </Stack>
              {subscription && (
                <Stack>
                  <Typography variant='captionRegular' color='text.secondary'>
                    Subscription status
                  </Typography>
                  <Typography variant='bodyRegular'>
                    {`${subscription.autoRenew ? 'Renews on' : 'Expires on'} ${DateTime.fromISO(
                      subscription.expireOn,
                    ).toFormat('LLL dd, yyyy')}`}
                  </Typography>
                </Stack>
              )}
              {!subscription && (
                <Button id='add-subscription' onClick={handleAddSubscription} variant='outlined'>
                  Add Subscription
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} flexWrap='wrap'>
          {darkMode && (
            <Card sx={{ flex: 1 }}>
              <CardHeader
                title='Appearance'
                titleTypographyProps={{ variant: 'titleMedium' }}
                subheader='Customize your experience'
                subheaderTypographyProps={{ variant: 'paragraphRegular', color: 'textSecondary' }}
                sx={{ paddingBottom: 1 }}
              />
              <CardContent sx={{ paddingTop: 1 }}>
                <Stack gap={2} direction={'row'} alignItems='center' flexWrap='wrap'>
                  <FormControlLabel
                    className={classes.themeContainer}
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'light' ? theme.palette.primary.light : theme.palette.background.default,
                    }}
                    id='theme-choice-light'
                    checked={Boolean(theme.palette.mode === 'light')}
                    control={
                      <FormControlLabel
                        control={<Radio />}
                        label={<Typography variant='paragraphMedium'>Light Mode</Typography>}
                      />
                    }
                    label={
                      <Avatar
                        sizes='large'
                        variant='rounded'
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? theme.palette.primary.main : '',
                          color: theme.palette.common.white,
                        }}
                      >
                        <LightMode />
                      </Avatar>
                    }
                    labelPlacement='top'
                    onChange={(_event, value) => {
                      if (value) {
                        setDatasetPreference({ theme: 'muiDefault' });
                      }
                    }}
                  />
                  <FormControlLabel
                    className={classes.themeContainer}
                    id='theme-choice-dark'
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.background.default,
                    }}
                    checked={Boolean(theme.palette.mode === 'dark')}
                    control={
                      <FormControlLabel
                        control={<Radio />}
                        label={<Typography variant='paragraphMedium'>Dark Mode</Typography>}
                      />
                    }
                    label={
                      <Avatar
                        variant='rounded'
                        sizes='large'
                        sx={{
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.primary.main
                              : theme.palette.background.layer2,
                          color: theme.palette.common.white,
                        }}
                      >
                        <DarkMode />
                      </Avatar>
                    }
                    labelPlacement='top'
                    onChange={(_event, value) => {
                      if (value) {
                        tracker.track(tracker.events.darkMode);
                        setDatasetPreference({ theme: 'ACME_DARK' });
                      }
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
          {privacyMode && (
            <Card sx={{ flex: 1 }}>
              <CardHeader
                title='Privacy Mode'
                titleTypographyProps={{ variant: 'titleMedium' }}
                subheader='Hide all the dollar values throughout the app'
                subheaderTypographyProps={{ variant: 'paragraphRegular', color: 'textSecondary' }}
                sx={{ paddingBottom: 1 }}
              />
              <CardContent sx={{ paddingTop: 1 }}>
                <Typography variant='paragraphRegular' color='text.secondary'>
                  Payee names and percentages can still be visible.
                </Typography>
                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems='center'>
                  <Typography variant='captionMedium' color={sPrivacyMode ? 'textPrimary' : 'text.disabled'}>
                    {sPrivacyMode ? 'Privacy mode on' : 'Privacy mode off'}
                  </Typography>
                  <Switch
                    label='Privacy mode'
                    checked={Boolean(sPrivacyMode)}
                    onChange={(event, checked) => {
                      if (checked) {
                        setSPrivacyMode(checked);
                        tracker.track(tracker.events.privacyMode, { state: checked, location: 'Settings' });
                      } else {
                        showConfirmationDialog({
                          title: 'Switch off Privacy mode?',
                          content: 'With privacy mode off, actual dollar amounts will become visible in Simplifi.',
                          confirmLabel: 'Switch off',
                          confirmAction: () => {
                            setSPrivacyMode(checked);
                            tracker.track(tracker.events.privacyMode, { state: checked, location: 'Settings' });
                          },
                        });
                      }
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
          {aiEnabled && (
            <Card sx={{ flex: 1 }}>
              <CardHeader
                title='Quicken Assist'
                titleTypographyProps={{ variant: 'titleMedium' }}
                subheader='Unlock personalized insights and summaries powered by AI'
                subheaderTypographyProps={{ variant: 'paragraphRegular', color: 'textSecondary' }}
                sx={{ paddingBottom: 1 }}
              />
              <CardContent sx={{ paddingTop: 1 }}>
                <Typography variant='paragraphRegular'>
                  When enabled, Quicken Assist and its features will be accessible across your entire financial space in
                  Quicken Simplifi.
                </Typography>
                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems='center'>
                  <Typography variant='captionMedium' color={AIEnabled ? 'textPrimary' : 'text.disabled'}>
                    {AIEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Switch
                    label='Enable Quicken Assist'
                    checked={Boolean(AIEnabled)}
                    onChange={(event, checked) => {
                      if (checked) {
                        tracker.track('AIOn');
                        setAIEnabled(checked);
                      } else {
                        showConfirmationDialog({
                          title: 'Disable Quicken Assist',
                          content:
                            ' When disabled, Quicken Assist and its features will not be accessible across your entire financial space in Quicken Simplifi. You can re-enable it anytime.',
                          confirmLabel: 'Confirm',
                          confirmAction: () => {
                            tracker.track('AIOff');
                            setAIEnabled(checked);
                          },
                        });
                      }
                    }}
                  />
                </Stack>
                <Typography variant='captionRegular' color='text.secondary'>
                  Please be advised that AI-generated content may occasionally include errors, omissions, or
                  inaccuracies. You remain solely responsible for verifying the accuracy of any AI-generated information
                  prior to reliance thereon.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
        <Card>
          <CardHeader
            title='Early Access'
            titleTypographyProps={{ variant: 'titleMedium' }}
            subheader='Be the first to try the latest Quicken Simplifi innovations!'
            subheaderTypographyProps={{ variant: 'paragraphRegular', color: 'textSecondary' }}
            sx={{ paddingBottom: 1 }}
          />
          <CardContent sx={{ paddingTop: 1 }}>
            <Dump obj={authSession} />
            <Stack gap={1} justifyContent={'flex-start'} alignItems='flex-start'>
              <Typography variant='paragraphRegular'>
                Enjoy exclusive access to upcoming features before they officially launch —plus, you can turn this off
                anytime without affecting your data.
              </Typography>
              <FormControlLabel
                id='register-for-beta'
                disabled={!authSession?.groups}
                checked={Boolean(authSession.groups?.includes('qcs_acme_beta'))}
                control={<QSwitch color='primary' showUncheckProgress showCheckProgress />}
                label={
                  <Typography
                    variant='captionMedium'
                    color={authSession.groups?.includes?.('qcs_acme_beta') ? 'textPrimary' : 'text.disabled'}
                  >
                    {authSession.groups?.includes?.('qcs_acme_beta') ? "You're enrolled" : 'Not enrolled'}
                  </Typography>
                }
                labelPlacement='start'
                onChange={(_event, value) => {
                  if (value) {
                    const registerForBetaPromise = actionHelpers.bindPromiseAction(
                      dispatch,
                      userInfoActions.userRegisterMeForBeta,
                    );
                    registerForBetaPromise({ clientId: getEnvironmentConfig().client_id })
                      .then((_response) => {
                        dispatch(userInfoActions.getUserInfo());
                      })
                      .catch((_error) => {
                        dispatch(userInfoActions.getUserInfo());
                      });
                  } else {
                    const unregisterForBetaPromise = actionHelpers.bindPromiseAction(
                      dispatch,
                      userInfoActions.userUnregisterMeForBeta,
                    );
                    unregisterForBetaPromise({ clientId: getEnvironmentConfig().client_id })
                      .then((_response) => {
                        dispatch(userInfoActions.getUserInfo());
                      })
                      .catch((_error) => {
                        dispatch(userInfoActions.getUserInfo());
                      });
                  }
                }}
              />
              <Typography variant='captionRegular' color='text.secondary'>
                Please keep in mind that these features are updated more frequently and are tested less.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
}

export default Profile;
